import { ArrowRight, Calendar, Users, User, Building2, GraduationCap, HeartPulse, Briefcase, BookOpen } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import TypeformWidget from "@/components/ui/TypeformWidget";
import doctorImage from "@/assets/doctor-image.jpg";
import midiLogo from "@/assets/midi_logo.png";
import superpowerLogo from "@/assets/superpower_logo.png";
import cleanMarketLogo from "@/assets/clean_market_logo.png";

export default function Hero() {
  return (
    <header className="relative overflow-hidden">
      {/* Colored accent bar at top */}
      <div className="bg-indigo-950 py-4">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-center text-xs text-indigo-100">
            Pioneering the future of advanced longevity medicine
          </p>
        </div>
      </div>
      
      <div className="bg-white text-gray-900 py-16 md:py-20 relative">
        {/* Subtle background pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-pattern"></div>
        </div>
      
        <div className="container relative z-20 mx-auto px-4 max-w-5xl">
          {/* Hero intro section */}
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-serif font-medium mb-6 leading-tight tracking-tight">
              <span className="text-indigo-950">High-impact longevity strategy—</span><br />
              <span className="text-indigo-950">for patients, clinicians, and companies committed to transformation.</span>
            </h1>
            <div className="h-px bg-indigo-900/20 w-16 mx-auto mb-8"></div>
            <p className="text-sm text-gray-700 max-w-2xl mx-auto mb-8 leading-relaxed">
              Proven frameworks, cutting-edge protocols, and strategic guidance to navigate the rapidly evolving field of longevity medicine.
            </p>
            
            {/* Typeform Embed */}
            <div className="max-w-xl mx-auto mb-8">
              <TypeformWidget formId="01JTNZY6A7DKP7B2421XSZ8AK5" />
            </div>
          </div>
          
          {/* Three pathway cards with professional styling */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {/* Card 1: Patient Pathway */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">01</p>
                <h3 className="text-base font-semibold text-gray-900">For Patients</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Access cutting-edge longevity protocols and personalized guidance to optimize your health and extend your healthspan.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Personalized longevity protocols</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Leading-edge interventions</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Community & expert guidance</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6 flex flex-col gap-2">
                <Link href="/patients">
                  <Button className="w-full bg-indigo-950 text-white hover:bg-indigo-900 text-xs px-4 py-2 rounded-sm h-auto">
                    Explore Offerings
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Card 2: Clinic/Business Pathway */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">02</p>
                <h3 className="text-base font-semibold text-gray-900">For Clinics & Businesses</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Transform your clinical approach with strategic consulting, operational systems, and cutting-edge longevity protocols.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Optimize practice operations</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Implement cutting-edge protocols</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Scale your longevity practice</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6 flex flex-col gap-2">
                <a href="#contact">
                  <Button className="w-full bg-indigo-950 text-white hover:bg-indigo-900 text-xs px-4 py-2 rounded-sm h-auto">
                    Apply for a Strategy Session
                  </Button>
                </a>
                <Link href="/consulting">
                  <Button className="w-full bg-white text-indigo-950 hover:bg-gray-50 border border-indigo-950 text-xs px-4 py-2 rounded-sm h-auto">
                    Learn About Consulting
                  </Button>
                </Link>
                <Link href="/mentorship">
                  <Button className="w-full bg-white text-indigo-950 hover:bg-gray-50 border border-indigo-950 text-xs px-4 py-2 rounded-sm h-auto">
                    Learn About Mentorship
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Card 3: Learning Pathway */}
            <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center">
                <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mr-3">03</p>
                <h3 className="text-base font-semibold text-gray-900">For Learners</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Access comprehensive educational resources on peptides, hormone optimization, and cutting-edge longevity protocols.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>On-demand clinical education</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Molecular & clinical perspectives</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-3 w-3 text-indigo-950 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Practical implementation guides</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6">
                <Button className="w-full bg-indigo-300 text-white hover:bg-indigo-300 cursor-not-allowed text-xs px-4 py-2 rounded-sm h-auto relative overflow-hidden group" disabled>
                  <span className="relative z-10">Browse Courses</span>
                  <span className="absolute inset-0 flex items-center justify-center w-full h-full bg-indigo-400 text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Coming Soon
                  </span>
                </Button>
              </div>
            </div>
          </div>
          
          {/* Social Proof Bar with professional styling */}
          <div className="mt-12 md:mt-16 pt-8 border-t border-gray-200">
            <p className="text-[11px] uppercase tracking-wider text-center mb-8 text-gray-500 font-medium">
              Trusted by Leading Organizations Building the Future of Health
            </p>
            <div className="flex flex-wrap justify-center items-center gap-x-24 gap-y-12">
              <div className="h-20 relative">
                <div className="grayscale hover:grayscale-0 transition-all duration-300">
                  <img src={midiLogo} alt="Midi Health" className="h-16 object-contain" />
                </div>
              </div>
              <div className="h-20 relative">
                <div className="grayscale hover:grayscale-0 transition-all duration-300">
                  <img src={superpowerLogo} alt="Superpower" className="h-20 object-contain" />
                </div>
              </div>
              <div className="h-20 relative">
                <div className="grayscale hover:grayscale-0 transition-all duration-300">
                  <img src={cleanMarketLogo} alt="Clean Market" className="h-12 object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}